
import { defineComponent, h, PropType, ref, computed } from 'vue';
import { FormInst, FormItemRule, SelectOption } from 'naive-ui';
import { notification, toast } from '../utils/notification.util';
import HoundSelectOption from './shared/HoundSelectOption.vue';
import BaseNotificationContent from './shared/BaseNotificationContent.vue';
import { http } from '../services';
import { MicroPayment } from '../../../common/ethers-v5/Queues';
import { HoundModel, FindAllResponse, QueueModel, emptyAddress, formatNumberDecimals } from '../../../common';
import { EthersService as eth, convertToUSD } from '../services';
import { exchangeRatesAvailable, exchangeRates } from '@/data/global';
import { BigNumber } from 'ethers';
import { getCurrencyLabel, overrideInventory } from '../../../common/inventories/index';
import breakpoints from '../utils/breakpoints';
import { Circle as CircleIcon } from '@vicons/fa';

export default defineComponent({
  emits: ['cancel', 'join'],
  props: {
    queue: {
      type: Object as PropType<QueueModel>,
      default: {},
      required: true
    }
  },
  components: { CircleIcon },
  setup(props, { emit }) {
    const formRef = ref<FormInst | null>(null);
    const cancel = () => {
      emit('cancel');
    };

    const formValue = ref({
      hound: null
    });

    const houndOptions = ref([]);
    const hounds = ref<HoundModel[]>([]);

    const queue = props.queue as QueueModel;

    const loading = ref<boolean>(false);

    http.getOwnHounds({
      limit: null,
      offset: null,
      orderBy: 'birthDate-DESC',
      running: false,
      minStamina: queue.staminaCost
    }).then((res: FindAllResponse<HoundModel>) => {
      res.data.forEach(el => {
        hounds.value.push(new HoundModel(el));
      })
      houndOptions.value = res.data.map((h: HoundModel) => ({
        value: h.token_id,
        label: h.name,
        disabled: queue.speciesAllowed.length && !queue.speciesAllowed.includes(h.specie)
      }));
    });

    const renderLabel = (option: SelectOption) => {
      const hound = hounds.value.find(h => h.token_id === option.value);
      return h(
        HoundSelectOption,
        {
          hound: hound,
          style: 'height: 5rem',
          infoMarginLeft: breakpoints.sDown.matches ? '7rem' : '10rem',
          ellipsisSmMax: breakpoints.sDown.matches ? '12.5rem' : '10rem',
          disabled: queue.speciesAllowed.length && !queue.speciesAllowed.includes(hound.specie)
        }
      )
    };

    const renderTag = ({ option }) => {
      const hound = hounds.value.find(h => h.token_id === option.value);
      return h(
        HoundSelectOption,
        {
          hound: hound,
          style: 'height: 5rem',
          key: hound.id,
          infoMarginLeft: breakpoints.sDown.matches ? '6rem' : '10rem',
          ellipsisSmMax: breakpoints.sDown.matches ? '12.5rem' : '10rem'
        }
      );
    };

    const currencyName = eth.currenciesInfo.find(c => c.value === queue.entryFeeCurrency)?.label;
    const currencyId = eth.currenciesInfo.find(c => c.value === queue.entryFeeCurrency)?.value;

    const registerHound = () => {
      formRef.value?.validate(async (errors) => {
        if (errors) {
          toast.error('Invalid values');
          return;
        }

        try {
          loading.value = true;

          const enqueueCost: [
            MicroPayment.StructStructOutput, 
            MicroPayment.StructStructOutput, 
            MicroPayment.StructStructOutput
          ] = await eth.queues.getEnqueueCost(queue.queue_id);

          let totalDefaultCurrencyToPay: BigNumber = BigNumber.from(0);
          let customApprovalsToMake: Array<{ currency: string, amount: string | BigNumber }> = [];

          for ( let i = 0 , l = enqueueCost.length ; i < l ; ++i ) {
            if ( enqueueCost[i].currency === emptyAddress ) {
              totalDefaultCurrencyToPay = totalDefaultCurrencyToPay.add(enqueueCost[i].amount);
            } else {
              let exists = -1;
              for ( let j = 0 , k = customApprovalsToMake.length ; j < k ; ++j ) {
                if ( customApprovalsToMake[j].currency === enqueueCost[i].currency ) {
                  exists = j;
                  break;
                }
              }
              if ( exists === -1 ) {
                customApprovalsToMake.push({
                  currency: enqueueCost[i].currency,
                  amount: enqueueCost[i].amount
                });
              } else {
                customApprovalsToMake[exists].amount = BigNumber.from(customApprovalsToMake[exists].amount).add(enqueueCost[i].amount);
              }
            }
          }

          for ( let i = 0 , l = customApprovalsToMake.length ; i < l ; ++i ) {
            const customTokenInterface = eth.getCustomTokenInterface(customApprovalsToMake[i].currency);
            const alreadyApproved = await customTokenInterface.allowance(eth.userAddress, eth.contractAddresses.PAYMENTS);
              if (alreadyApproved.lt(customApprovalsToMake[i].amount)) {
                const approveTx = await eth.getCustomTokenInterface(
                  customApprovalsToMake[i].currency
                ).approve(
                  eth.contractAddresses.PAYMENTS, 
                  BigNumber.from("1000000000000000000").gt(customApprovalsToMake[i].amount) ? "1000000000000000000" : customApprovalsToMake[i].amount
                );
                await approveTx.wait();
              }
          }

          const tx = await eth.queues.enqueue(
            queue.queue_id,
            formValue.value.hound, {
              value: totalDefaultCurrencyToPay,
              gasLimit: 700_000
            }
          );

          await tx.wait();
          emit('join');

          const distinctCosts: Array<{ amount: BigNumber, currency: string }> = [];
          enqueueCost.forEach(bc => {
            const bcf = { amount: bc.amount, currency: bc.currency };
            const existingCost = distinctCosts.find(dc => dc.currency === bc.currency);
            if (existingCost) existingCost.amount = BigNumber.from(existingCost.amount).add(bc.amount);
            else distinctCosts.push(bcf);
          })
          const selectedHound = hounds.value.find(el => el.token_id === formValue.value.hound);
          notification.create({
            title: 'Joined Queue',
            description: `You've successfully joined ${queue.name}. The race will be held on ${queue.arena.name} when all remaining participant slots are filled.`,
            content: () => h(
              BaseNotificationContent,
              {
                infoElements: [
                  { label: 'Queue', content: queue.name },
                  { label: 'Arena', content: queue.arena.name },
                  { label: 'Hound', content: selectedHound.name },
                  {
                    label: 'Entry Fee',
                    content: distinctCosts
                      .map(cost => {
                        if (cost.amount.gt(0)) {
                          return `${eth.utils.formatEther(cost.amount)} ${getCurrencyLabel(eth.chainId, cost.currency)}`
                        }
                      })
                      .join(' | ')
                  }
                ]
              }
            ),
            meta: new Date().toUTCString()
          });
          toast.success('Successfully registered hound in race');
          loading.value = false;
          cancel();
        } catch(err) {
          loading.value = false;
          toast.error('Transaction Failed');
          console.log(err);
        }
      })
    };

    const rules = {
      hound: {
        trigger: ['blur', 'input'],
        validator: (rule: FormItemRule, value: any) => {
          return new Promise<void>((resolve, reject) => {
            if (value === null) reject(Error('Hound is required'));
            resolve();
          });
        }
      }
    }

    const totalTicketFee = computed(() => {
      const arenaFee = BigNumber.from(queue.arena.fee).div(queue.totalParticipants);
      const entryFee = BigNumber.from(queue.entryFee);
      const totalFee = arenaFee.add(entryFee);
      return formatNumberDecimals(eth.utils.formatEther(totalFee));
    });

    const alphaDuneFeeInfo = computed(() => {
      return {
        amount: formatNumberDecimals(eth.utils.formatEther(BigNumber.from(queue.alphaDuneFee).div(queue.totalParticipants))),
        currency: queue.alphaDuneFeeCurrency,
        currencyName: eth.currenciesInfo.find(c => c.value === queue.alphaDuneFeeCurrency)?.label
      };
    });

    return {
      formRef,
      formValue,
      cancel,
      registerHound,
      rules,
      loading,
      houndOptions,
      renderLabel,
      renderTag,
      totalTicketFee,
      alphaDuneFeeInfo,
      currencyName,
      currencyId,
      convertToUSD,
      exchangeRatesAvailable,
      exchangeRates,
      breakpoints,
      overrideInventory: overrideInventory
    };
  },
})
